import { create } from "zustand";
import { devtools } from "zustand/middleware";
import type { ElementData } from "@/types/preview-message.types";
import type {
  HistorySnapshot,
  HistoryActionType,
  StructureChangeType,
} from "@/types/visual-editor-history.types";

interface VisualEditorHistoryStore {
  // History
  history: HistorySnapshot[];
  currentIndex: number;
  maxHistorySize: number;

  // Actions
  pushHistory: (
    elementId: string,
    elementSelector: string,
    changes: HistorySnapshot["pendingChanges"],
    fullElementData: ElementData,
    actionType: HistoryActionType,
    structureChange?: StructureChangeType,
    batchId?: string,
  ) => void;
  undo: () => HistorySnapshot | null;
  redo: () => HistorySnapshot | null;
  canUndo: () => boolean;
  canRedo: () => boolean;
  clearHistory: () => void;
  resetHistory: (
    elementId: string,
    elementSelector: string,
    initialChanges: HistorySnapshot["pendingChanges"],
    fullElementData: ElementData,
  ) => void;
}

export const useVisualEditorHistoryStore = create<VisualEditorHistoryStore>()(
  devtools(
    (set, get) => ({
      history: [],
      currentIndex: -1,
      maxHistorySize: 50,

      // Push new state to history
      pushHistory: (
        elementId,
        elementSelector,
        changes,
        fullElementData,
        actionType = "update",
        structureChange,
        batchId,
      ) =>
        set(
          (state) => {
            const newHistory = state.history.slice(0, state.currentIndex + 1);

            // Check if this is the first time we're pushing this element
            const isFirstPush = !newHistory.some(
              (h) => h.elementId === elementId,
            );

            // For 'add' action: determine if this is initial add or subsequent update
            let finalStructureChange = structureChange;
            let isInitialAdd = false;

            if (actionType === "add") {
              // Find if there's already an 'add' snapshot for this element
              const existingAddSnapshot = [...newHistory]
                .reverse()
                .find(
                  (h) => h.elementId === elementId && h.actionType === "add",
                );

              if (existingAddSnapshot) {
                // This is a subsequent update to an added element
                // Preserve structureChange from the initial add
                finalStructureChange = existingAddSnapshot.structureChange;
                isInitialAdd = false;
              } else {
                // This is the initial add (first time seeing this element with 'add' action)
                isInitialAdd = true;
                finalStructureChange = structureChange;
              }
            }

            // Add new snapshot
            newHistory.push({
              elementId,
              elementSelector,
              pendingChanges: structuredClone(changes), // Deep clone
              fullElementData: structuredClone(fullElementData), // Deep clone
              originalElementData: isFirstPush
                ? structuredClone(fullElementData)
                : undefined,
              timestamp: Date.now(),
              actionType,
              structureChange: finalStructureChange
                ? structuredClone(finalStructureChange)
                : undefined,
              isInitialAdd: actionType === "add" ? isInitialAdd : undefined,
              batchId,
            });

            // Limit history size
            if (newHistory.length > state.maxHistorySize) {
              newHistory.shift();
            }

            return {
              history: newHistory,
              currentIndex: newHistory.length - 1,
            };
          },
          false,
          `pushHistory/${actionType}`,
        ),

      // Undo - return full snapshot previous
      undo: () => {
        const state = get();
        if (!state.canUndo()) return null;

        const newIndex = state.currentIndex - 1;
        set({ currentIndex: newIndex }, false, "undo");

        // Special case: going from index 0 to -1, return original state
        if (newIndex === -1 && state.history[0]?.originalElementData) {
          console.log(
            "[VisualEditorHistory] Undo to -1: returning originalElementData",
            state.history[0].originalElementData,
          );
          return {
            ...structuredClone(state.history[0]),
            fullElementData: structuredClone(
              state.history[0].originalElementData,
            ),
            pendingChanges: {}, // No pending changes in original state
          };
        }

        const snapshot = structuredClone(state.history[newIndex]);
        console.log(
          "[VisualEditorHistory] Undo to index",
          newIndex,
          ":",
          snapshot,
        );
        return snapshot;
      },

      // Redo - return full snapshot continue
      redo: () => {
        const state = get();
        if (!state.canRedo()) return null;

        const newIndex = state.currentIndex + 1;
        set({ currentIndex: newIndex }, false, "redo");

        return structuredClone(state.history[newIndex]);
      },

      canUndo: () => {
        const state = get();
        // Can undo when currentIndex >= 0 (have changes to undo)
        // For add/delete/move operations, we can undo even from index 0 back to index -1 (original state)
        // For update operations, we can undo when currentIndex > 0
        return state.currentIndex >= 0;
      },

      canRedo: () => {
        const state = get();
        // Normal case: can redo when not at end of history
        if (state.currentIndex < state.history.length - 1) return true;

        // Special case: can redo from index -1 if there are operations in history
        // This handles the case where user undoes to before the first operation
        return state.currentIndex === -1 && state.history.length > 0;
      },

      clearHistory: () =>
        set(
          {
            history: [],
            currentIndex: -1,
          },
          false,
          "clearHistory",
        ),

      // Reset history with initial state
      resetHistory: (
        elementId,
        elementSelector,
        initialChanges,
        fullElementData,
      ) =>
        set(
          {
            history: [
              {
                elementId,
                elementSelector,
                pendingChanges: structuredClone(initialChanges),
                fullElementData: structuredClone(fullElementData),
                timestamp: Date.now(),
                actionType: "update",
              },
            ],
            currentIndex: 0,
          },
          false,
          "resetHistory",
        ),
    }),
    {
      name: "VisualEditorHistory",
      enabled: true,
    },
  ),
);
